/**
 * App access (ADR-0010 P4.5b) — roles & members of one app.
 *
 * Two sections:
 *  - **Roles**: personas inside the app (Agent, Customer, …). Create /
 *    delete custom roles; built-in roles are undeletable.
 *  - **Members**: a principal granted a role. Three realms (ADR-0010 P7):
 *    `tray_user` / `tray_group` (operators) and `app_user` (portal
 *    end-users). The `PrincipalPicker` resolves each by name instead of a
 *    raw id, and can create a portal `app_user` inline.
 *
 * This is the *usage*-plane admin — it decides what a member can do
 * inside the app. The management plane (who can build the app) stays on
 * the tag-scoped `apps` RBAC resource.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  BookOpen,
  Database,
  IdCard,
  Layers,
  Plus,
  Shield,
  ShieldCheck,
  Table2,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Switch } from "@/components/ui/switch";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { slugify } from "@/pages/AppsPage";
import {
  appsApi,
  type MemberCreate,
  type MemberOut,
  type PrincipalType,
  type RoleCreate,
  type RoleOut,
} from "@/lib/api-apps";
import { appUsersApi } from "@/lib/api-app-users";
import { usersApi } from "@/lib/api-users";
import { usePermissions } from "@/lib/permissions";
import { AccessMatrixSection } from "@/components/apps/AccessMatrixSection";
import { MemberAttrsDialog, ProfileFieldsSection } from "@/components/apps/ProfileSection";


const PRINCIPAL_TYPES: PrincipalType[] = ["tray_user", "tray_group", "app_user"];

const PRINCIPAL_TYPE_LABEL: Record<PrincipalType, string> = {
  tray_user: "Tray user",
  tray_group: "Tray group",
  app_user: "App user (portal)",
};

const selectClass =
  "block w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm";


export function AppAccessPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { appId = "" } = useParams<{ appId: string }>();
  const { can } = usePermissions();
  const canWrite = can("apps", "write");

  const appQ = useQuery({
    queryKey: ["apps", appId],
    queryFn: () => appsApi.get(appId),
    enabled: !!appId,
  });

  return (
    <PageContainer>
      <PageHeader
        title={t("apps.accessTitle", "Members & roles")}
        description={appQ.data?.name}
        actions={
          <Button variant="ghost" size="sm" onClick={() => navigate(`/apps/${appId}`)}>
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            {t("apps.backToApp", "Back to app")}
          </Button>
        }
      />
      <PageContent>
        {/* AppAccessSections is a full-height two-column IDE (ADR-0059); the
            deep-link route bounds its height so the flex layout resolves. */}
        <div className="h-[calc(100vh-14rem)] overflow-hidden rounded-lg border border-border/60">
          <AppAccessSections appId={appId} canWrite={canWrite} />
        </div>
      </PageContent>
    </PageContainer>
  );
}

/**
 * The roles + members management, extracted so the app-detail screen can render
 * it INLINE as the "Membri e ruoli" sub-nav group (same right-pane treatment as
 * Dati / Logica / Interfaccia — no modal, no navigating away). The standalone
 * `/access` route still wraps this for deep-links.
 */
/** Membri e ruoli — a two-column IDE: a left nav (Ruoli · Membri · Campi profilo ·
 *  Accesso ai dati [Tabelle · Viste · Dataset · Basi di conoscenza]) + the right
 *  panel showing ONLY the selected section (ADR-0059 readability rework). */
type AccessSection =
  | "roles"
  | "members"
  | "profile"
  | "data:table"
  | "data:view"
  | "data:dataset"
  | "data:kb";

export function AppAccessSections({ appId, canWrite }: { appId: string; canWrite: boolean }) {
  const [section, setSection] = React.useState<AccessSection>("roles");
  const item = (key: AccessSection, Icon: typeof Shield, label: string, sub = false) => (
    <button
      key={key}
      type="button"
      onClick={() => setSection(key)}
      className={
        "flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm transition " +
        (sub ? "pl-7 " : "") +
        (section === key
          ? "bg-accent font-medium text-accent-foreground"
          : "text-muted-foreground hover:bg-surface hover:text-foreground")
      }
    >
      <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
      <span className="truncate">{label}</span>
    </button>
  );

  return (
    <div className="flex h-full">
      <nav className="w-56 shrink-0 space-y-0.5 overflow-y-auto border-r border-border/60 p-2">
        {item("roles", Shield, "Ruoli")}
        {item("members", Users, "Membri")}
        {item("profile", IdCard, "Campi profilo")}
        <div className="px-2.5 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">
          <span className="flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.75} />
            Accesso ai dati
          </span>
        </div>
        {item("data:table", Table2, "Tabelle", true)}
        {item("data:view", Layers, "Viste", true)}
        {item("data:dataset", Database, "Dataset", true)}
        {item("data:kb", BookOpen, "Basi di conoscenza", true)}
      </nav>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {section === "roles" && <RolesSection appId={appId} canWrite={canWrite} />}
        {section === "members" && <MembersSection appId={appId} canWrite={canWrite} />}
        {section === "profile" && <ProfileFieldsSection appId={appId} canWrite={canWrite} />}
        {section === "data:table" && (
          <AccessMatrixSection appId={appId} canWrite={canWrite} surface="table" />
        )}
        {section === "data:view" && (
          <AccessMatrixSection appId={appId} canWrite={canWrite} surface="view" />
        )}
        {section === "data:dataset" && (
          <AccessMatrixSection appId={appId} canWrite={canWrite} surface="dataset" />
        )}
        {section === "data:kb" && (
          <AccessMatrixSection appId={appId} canWrite={canWrite} surface="kb" />
        )}
      </div>
    </div>
  );
}


function RolesSection({ appId, canWrite }: { appId: string; canWrite: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = React.useState(false);

  const rolesQ = useQuery({
    queryKey: ["apps", appId, "roles"],
    queryFn: () => appsApi.listRoles(appId),
  });

  const remove = useMutation({
    mutationFn: (roleId: string) => appsApi.deleteRole(appId, roleId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["apps", appId, "roles"] });
      qc.invalidateQueries({ queryKey: ["apps", appId, "members"] });
    },
  });

  // App-admin flag (ADR-0052 S4b): members of a `can_admin` role can provision
  // app-scoped service accounts for this app from the portal.
  const toggleAdmin = useMutation({
    mutationFn: (r: RoleOut) => appsApi.updateRole(appId, r.id, { can_admin: !r.can_admin }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["apps", appId, "roles"] }),
  });

  const roles = rolesQ.data ?? [];

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
          <Shield className="h-4 w-4" strokeWidth={1.5} />
          {t("apps.roles", "Roles")}
        </h3>
        {canWrite && (
          <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" strokeWidth={1.5} />
            {t("apps.newRole", "New role")}
          </Button>
        )}
      </div>
      {canWrite && roles.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {t(
            "apps.roleAdminHelp",
            "«Amministratore app»: i membri con questo ruolo possono creare service account (chiavi API) per questa app dal portale.",
          )}
        </p>
      )}
      {roles.length === 0 && !rolesQ.isLoading ? (
        <EmptyState
          icon={Shield}
          title={t("apps.noRoles", "No roles yet")}
          description={t("apps.noRolesDescription", "Add personas like Agent or Customer to grant to members.")}
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border/60">
          <Table>
            <Thead>
              <Tr>
                <Th>{t("apps.roleName", "Role")}</Th>
                <Th className="w-40">{t("apps.slug", "Slug")}</Th>
                {canWrite && (
                  <Th className="w-44">{t("apps.roleAdminCol", "Amministratore app")}</Th>
                )}
                {canWrite && <Th className="w-16 text-right">{t("common.actions", "Actions")}</Th>}
              </Tr>
            </Thead>
            <Tbody>
              {roles.map((r) => (
                <Tr key={r.id}>
                  <Td>
                    <div className="flex items-center gap-2 font-medium">
                      {r.name}
                      {r.is_builtin && (
                        <Badge tone="neutral" className="text-[10px]">
                          {t("apps.builtin", "built-in")}
                        </Badge>
                      )}
                    </div>
                    {r.description && (
                      <div className="text-xs text-muted-foreground">{r.description}</div>
                    )}
                  </Td>
                  <Td className="font-mono text-xs text-muted-foreground">{r.slug}</Td>
                  {canWrite && (
                    <Td>
                      <label className="flex items-center gap-2 text-xs">
                        <Switch
                          checked={r.can_admin}
                          disabled={toggleAdmin.isPending}
                          onCheckedChange={() => toggleAdmin.mutate(r)}
                          aria-label={t("apps.roleAdminCol", "Amministratore app")}
                        />
                        <span className="text-muted-foreground">
                          {r.can_admin
                            ? t("apps.roleAdminOn2", "Può creare service account app")
                            : t("apps.roleAdminOff2", "Solo membro")}
                        </span>
                      </label>
                    </Td>
                  )}
                  {canWrite && (
                    <Td className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {!r.is_builtin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={t("common.delete", "Delete")}
                            onClick={() => {
                              if (confirm(t("apps.confirmDeleteRole", 'Delete role "{{name}}"?', { name: r.name }))) {
                                remove.mutate(r.id);
                              }
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />
                          </Button>
                        )}
                      </div>
                    </Td>
                  )}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </div>
      )}
      <CreateRoleDialog appId={appId} open={open} onOpenChange={setOpen} />
    </section>
  );
}


function CreateRoleDialog({
  appId,
  open,
  onOpenChange,
}: {
  appId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = React.useState("");
  const [slug, setSlug] = React.useState("");
  const [slugTouched, setSlugTouched] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setName("");
      setSlug("");
      setSlugTouched(false);
    }
  }, [open]);

  const create = useMutation({
    mutationFn: (body: RoleCreate) => appsApi.createRole(appId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["apps", appId, "roles"] });
      onOpenChange(false);
    },
  });

  const effectiveSlug = slugTouched ? slug : slugify(name);
  const canSubmit = name.trim().length > 0 && effectiveSlug.length > 0 && !create.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("apps.newRole", "New role")}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="role-name">{t("apps.roleName", "Role")}</Label>
            <Input id="role-name" value={name} autoFocus placeholder="Agent" onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="role-slug">{t("apps.slug", "Slug")}</Label>
            <Input
              id="role-slug"
              value={effectiveSlug}
              className="font-mono"
              placeholder="agent"
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(slugify(e.target.value));
              }}
            />
          </div>
          {create.isError && (
            <Alert tone="danger" title={t("apps.createRoleFailed", "Could not create role")}>
              <span className="font-mono text-xs">{(create.error as Error).message}</span>
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button disabled={!canSubmit} onClick={() => create.mutate({ name: name.trim(), slug: effectiveSlug })}>
            {t("apps.create", "Create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function MembersSection({ appId, canWrite }: { appId: string; canWrite: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [profileMember, setProfileMember] = React.useState<MemberOut | null>(null);

  const rolesQ = useQuery({
    queryKey: ["apps", appId, "roles"],
    queryFn: () => appsApi.listRoles(appId),
  });
  const membersQ = useQuery({
    queryKey: ["apps", appId, "members"],
    queryFn: () => appsApi.listMembers(appId),
  });

  const roles = rolesQ.data ?? [];
  const roleName = (id: string) => roles.find((r) => r.id === id)?.name ?? id;

  const changeRole = useMutation({
    mutationFn: ({ memberId, roleId }: { memberId: string; roleId: string }) =>
      appsApi.updateMemberRole(appId, memberId, roleId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["apps", appId, "members"] }),
  });
  const remove = useMutation({
    mutationFn: (memberId: string) => appsApi.removeMember(appId, memberId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["apps", appId, "members"] }),
  });

  const members = membersQ.data ?? [];

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
          <UserPlus className="h-4 w-4" strokeWidth={1.5} />
          {t("apps.members", "Members")}
        </h3>
        {canWrite && roles.length > 0 && (
          <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" strokeWidth={1.5} />
            {t("apps.addMember", "Add member")}
          </Button>
        )}
      </div>
      {roles.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t("apps.addRolesFirst", "Add at least one role before assigning members.")}
        </p>
      ) : members.length === 0 && !membersQ.isLoading ? (
        <EmptyState
          icon={UserPlus}
          title={t("apps.noMembers", "No members yet")}
          description={t("apps.noMembersDescription", "Grant a Tray user or group a role inside this app.")}
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border/60">
          <Table>
            <Thead>
              <Tr>
                <Th className="w-32">{t("apps.principalType", "Type")}</Th>
                <Th>{t("apps.principal", "Principal")}</Th>
                <Th className="w-48">{t("apps.role", "Role")}</Th>
                {canWrite && <Th className="w-16 text-right">{t("common.actions", "Actions")}</Th>}
              </Tr>
            </Thead>
            <Tbody>
              {members.map((m) => (
                <Tr key={m.id}>
                  <Td className="text-xs text-muted-foreground">
                    {PRINCIPAL_TYPE_LABEL[m.principal_type] ?? m.principal_type}
                  </Td>
                  <Td className="font-medium">
                    {m.principal_label ?? m.principal_id}
                    {m.principal_label && (
                      <div className="font-mono text-[10px] text-muted-foreground">
                        {m.principal_id}
                      </div>
                    )}
                  </Td>
                  <Td>
                    {canWrite ? (
                      <select
                        value={m.role_id}
                        className={selectClass}
                        onChange={(e) => changeRole.mutate({ memberId: m.id, roleId: e.target.value })}
                      >
                        {roles.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      roleName(m.role_id)
                    )}
                  </Td>
                  {canWrite && (
                    <Td className="text-right">
                      {m.principal_type === "app_user" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={t("apps.editMemberProfile", "Profile")}
                          title={t("apps.editMemberProfile", "Profile")}
                          onClick={() => setProfileMember(m)}
                        >
                          <IdCard className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={t("apps.removeMember", "Remove")}
                        onClick={() => remove.mutate(m.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />
                      </Button>
                    </Td>
                  )}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </div>
      )}
      <AddMemberDialog appId={appId} roles={roles} open={open} onOpenChange={setOpen} />
      <MemberAttrsDialog
        appId={appId}
        member={profileMember}
        open={profileMember !== null}
        onOpenChange={(v) => !v && setProfileMember(null)}
      />
    </section>
  );
}


function AddMemberDialog({
  appId,
  roles,
  open,
  onOpenChange,
}: {
  appId: string;
  roles: RoleOut[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [principalType, setPrincipalType] = React.useState<PrincipalType>("tray_user");
  const [principalId, setPrincipalId] = React.useState("");
  const [roleId, setRoleId] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setPrincipalType("tray_user");
      setPrincipalId("");
      setRoleId(roles[0]?.id ?? "");
    }
  }, [open, roles]);

  const add = useMutation({
    mutationFn: (body: MemberCreate) => appsApi.addMember(appId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["apps", appId, "members"] });
      onOpenChange(false);
    },
  });

  const canSubmit = principalId.trim().length > 0 && roleId.length > 0 && !add.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("apps.addMember", "Add member")}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="m-type">{t("apps.principalType", "Type")}</Label>
            <select
              id="m-type"
              value={principalType}
              className={selectClass}
              onChange={(e) => {
                setPrincipalType(e.target.value as PrincipalType);
                setPrincipalId(""); // reset selection when the realm changes
              }}
            >
              {PRINCIPAL_TYPES.map((p) => (
                <option key={p} value={p}>
                  {PRINCIPAL_TYPE_LABEL[p]}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>{t("apps.principal", "Principal")}</Label>
            <PrincipalPicker
              principalType={principalType}
              value={principalId}
              onChange={setPrincipalId}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="m-role">{t("apps.role", "Role")}</Label>
            <select id="m-role" value={roleId} className={selectClass} onChange={(e) => setRoleId(e.target.value)}>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
          {add.isError && (
            <Alert tone="danger" title={t("apps.addMemberFailed", "Could not add member")}>
              <span className="font-mono text-xs">{(add.error as Error).message}</span>
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            disabled={!canSubmit}
            onClick={() =>
              add.mutate({
                principal_type: principalType,
                principal_id: principalId.trim(),
                role_id: roleId,
              })
            }
          >
            {t("apps.add", "Add")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/** One selectable principal — id + a friendly label. */
interface PrincipalOption {
  id: string;
  label: string;
  hint?: string;
}

/**
 * Searchable picker that resolves a principal by NAME instead of forcing the
 * operator to paste a raw id (ADR-0010 P7). It adapts to the principal realm:
 * `tray_user` → operator users, `tray_group` → operator groups, `app_user` →
 * portal end-users (with an inline "create" affordance, since those used to
 * be portal-signup-only).
 */
function PrincipalPicker({
  principalType,
  value,
  onChange,
}: {
  principalType: PrincipalType;
  value: string;
  onChange: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = React.useState("");
  const [creating, setCreating] = React.useState(false);

  React.useEffect(() => {
    setSearch("");
    setCreating(false);
  }, [principalType]);

  // app_user search hits the backend; tray realms are filtered client-side
  // (their list endpoints page without a search param).
  const appUsersQ = useQuery({
    queryKey: ["app-users", "pick", search],
    queryFn: () => appUsersApi.list({ search: search || undefined, page_size: 25 }),
    enabled: principalType === "app_user",
  });
  const usersQ = useQuery({
    queryKey: ["users", "pick"],
    queryFn: () => usersApi.list({ page_size: 100 }),
    enabled: principalType === "tray_user",
  });
  const groupsQ = useQuery({
    queryKey: ["groups", "pick"],
    queryFn: () => usersApi.listGroups({ page_size: 100 }),
    enabled: principalType === "tray_group",
  });

  let options: PrincipalOption[] = [];
  if (principalType === "app_user") {
    options = (appUsersQ.data?.items ?? []).map((u) => ({
      id: u.id,
      label: u.email,
      hint: u.full_name ?? undefined,
    }));
  } else if (principalType === "tray_user") {
    options = (usersQ.data?.items ?? []).map((u) => ({
      id: u.user_id,
      label: u.username,
      hint: u.email,
    }));
  } else {
    options = (groupsQ.data?.items ?? []).map((g) => ({ id: g.group_id, label: g.name, hint: g.group_id }));
  }

  // Client-side filter for the tray realms (app_user is server-filtered).
  const q = search.trim().toLowerCase();
  const filtered =
    principalType === "app_user" || !q
      ? options
      : options.filter((o) => o.label.toLowerCase().includes(q) || (o.hint ?? "").toLowerCase().includes(q));

  const selected = options.find((o) => o.id === value);

  if (creating) {
    return (
      <CreateAppUserInline
        onCancel={() => setCreating(false)}
        onCreated={(u) => {
          onChange(u.id);
          setCreating(false);
        }}
      />
    );
  }

  return (
    <div className="space-y-2">
      <Input
        value={search}
        autoFocus
        placeholder={t("apps.principalSearchHint", "Search by name or email…")}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="max-h-44 overflow-y-auto rounded-md border border-border/60">
        {filtered.length === 0 ? (
          <p className="px-2 py-3 text-xs text-muted-foreground">
            {t("apps.noPrincipals", "No matches.")}
          </p>
        ) : (
          filtered.map((o) => (
            <button
              type="button"
              key={o.id}
              onClick={() => onChange(o.id)}
              className={`flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left text-sm hover:bg-surface ${
                o.id === value ? "bg-accent/15" : ""
              }`}
            >
              <span className="truncate">
                {o.label}
                {o.hint && <span className="ml-2 text-xs text-muted-foreground">{o.hint}</span>}
              </span>
              {o.id === value && <span className="text-xs text-accent">✓</span>}
            </button>
          ))
        )}
      </div>
      {principalType === "app_user" && (
        <Button variant="ghost" size="sm" onClick={() => setCreating(true)}>
          <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
          {t("apps.createAppUser", "Create app user")}
        </Button>
      )}
      {selected && (
        <p className="text-xs text-muted-foreground">
          {t("apps.selected", "Selected")}: <span className="font-medium">{selected.label}</span>
        </p>
      )}
    </div>
  );
}


/** Inline "create a portal end-user" form, used from the member picker so an
 *  operator can provision + assign in one flow (the dedicated App Users page
 *  offers the same create). */
function CreateAppUserInline({
  onCreated,
  onCancel,
}: {
  onCreated: (u: { id: string; email: string }) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [email, setEmail] = React.useState("");
  const [fullName, setFullName] = React.useState("");
  const [password, setPassword] = React.useState("");

  const create = useMutation({
    mutationFn: () =>
      appUsersApi.create({
        email: email.trim(),
        full_name: fullName.trim() || null,
        password: password.trim() || null,
      }),
    onSuccess: (u) => {
      qc.invalidateQueries({ queryKey: ["app-users"] });
      onCreated({ id: u.id, email: u.email });
    },
  });

  const canSubmit = email.trim().length > 3 && !create.isPending;

  return (
    <div className="space-y-2 rounded-md border border-border/60 p-2">
      <Input value={email} autoFocus placeholder={t("apps.email", "Email")} onChange={(e) => setEmail(e.target.value)} />
      <Input
        value={fullName}
        placeholder={t("apps.fullNameOptional", "Full name (optional)")}
        onChange={(e) => setFullName(e.target.value)}
      />
      <Input
        type="password"
        value={password}
        placeholder={t("apps.passwordOptional", "Password (optional)")}
        onChange={(e) => setPassword(e.target.value)}
      />
      {create.isError && (
        <p className="font-mono text-xs text-danger">{(create.error as Error).message}</p>
      )}
      <div className="flex items-center gap-2">
        <Button size="sm" disabled={!canSubmit} onClick={() => create.mutate()}>
          {t("apps.create", "Create")}
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          {t("common.cancel", "Cancel")}
        </Button>
      </div>
    </div>
  );
}
